import type { AskUserToolInputDTO, AskUserToolOptionDTO, AskUserToolOutputDTO } from '@shared/schemas/chat-ask-user';
import { CHAT_ERROR_CODES } from '@shared/constants/error-codes';
import type { ChatUIMessage } from './types';

export type AskUserToolPart = Extract<ChatUIMessage['parts'][number], { type: 'tool-ask_user' }>;

const LEAKED_ASK_USER_JSON_PATTERN = /\{"options"\s*:\s*\[/;

export function sanitizeAssistantTextDisplay(text: string): string {
  const markerIndex = text.search(LEAKED_ASK_USER_JSON_PATTERN);
  if (markerIndex < 0) {
    return text;
  }

  return text.slice(0, markerIndex).trimEnd();
}

export type ComposerAskUserAction = 'intercept-text' | 'reject-attachments-only' | 'passthrough';

export type ToolContinuationErrorState = {
  output: AskUserToolOutputDTO;
  toolCallId: string;
};

function getLastStepStartIndex(parts: ChatUIMessage['parts']) {
  return parts.reduce((lastIndex, part, index) => (
    part.type === 'step-start' ? index : lastIndex
  ), -1);
}

export function isAskUserToolPart(part: ChatUIMessage['parts'][number]): part is AskUserToolPart {
  return part.type === 'tool-ask_user';
}

function getAskUserPartPriority(part: AskUserToolPart) {
  switch (part.state) {
    case 'output-available':
    case 'output-error':
      return 3;
    case 'input-available':
      return 2;
    case 'input-streaming':
      return 1;
    default:
      return 0;
  }
}

export function getAskUserToolParts(message: ChatUIMessage): AskUserToolPart[] {
  const bestByToolCallId = new Map<string, AskUserToolPart>();

  for (const part of message.parts) {
    if (!isAskUserToolPart(part)) {
      continue;
    }

    const existing = bestByToolCallId.get(part.toolCallId);

    if (!existing || getAskUserPartPriority(part) >= getAskUserPartPriority(existing)) {
      bestByToolCallId.set(part.toolCallId, part);
    }
  }

  const ordered: AskUserToolPart[] = [];
  const seen = new Set<string>();

  for (const part of message.parts) {
    if (!isAskUserToolPart(part) || seen.has(part.toolCallId)) {
      continue;
    }

    const resolved = bestByToolCallId.get(part.toolCallId);

    if (resolved) {
      ordered.push(resolved);
      seen.add(part.toolCallId);
    }
  }

  return ordered;
}

type ResolvableToolPart = { state: string } & ChatUIMessage['parts'][number];

function isToolDisplayPart(part: ChatUIMessage['parts'][number]): part is ResolvableToolPart {
  return (part.type === 'dynamic-tool' || part.type.startsWith('tool-')) && 'state' in part;
}

/**
 * Prédicat `sendAutomaticallyWhen` : ne relance une continuation que lorsqu'une
 * réponse `ask_user` vient d'être fournie. Le helper générique du SDK
 * (`lastAssistantMessageIsCompleteWithToolCalls`) se déclenche aussi pour les
 * tools résolus côté serveur (ex. `generate_document`), ce qui provoquait une
 * requête de continuation parasite après chaque génération documentaire.
 */
export function lastAssistantMessageIsCompleteWithAskUserOutputs({
  messages,
}: {
  messages: ChatUIMessage[];
}): boolean {
  const message = messages[messages.length - 1];

  if (!message || message.role !== 'assistant') {
    return false;
  }

  const lastStepParts = message.parts.slice(getLastStepStartIndex(message.parts) + 1);
  const askUserParts = lastStepParts.filter(isAskUserToolPart);

  if (askUserParts.length === 0) {
    return false;
  }

  return lastStepParts
    .filter(isToolDisplayPart)
    .every((part) => part.state === 'output-available' || part.state === 'output-error');
}

export function findPendingAskUserPart(messages: ChatUIMessage[]): AskUserToolPart | null {
  const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant');

  if (!lastAssistant) {
    return null;
  }

  const lastStepStartIndex = getLastStepStartIndex(lastAssistant.parts);
  const pendingPart = lastAssistant.parts
    .slice(lastStepStartIndex + 1)
    .filter(isAskUserToolPart)
    .find((part) => part.state === 'input-available');

  return pendingPart ?? null;
}

export function getMessageTextFromParts(parts: ChatUIMessage['parts']) {
  return parts
    .filter((part): part is Extract<ChatUIMessage['parts'][number], { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('\n')
    .trim();
}

export function messagePartsHaveFiles(parts: ChatUIMessage['parts']) {
  return parts.some((part) => part.type === 'file');
}

export function shouldInterceptComposerForAskUser(params: {
  hasReadyAttachments: boolean;
  pendingAskUser: boolean;
  trimmedInput: string;
}): ComposerAskUserAction {
  if (!params.pendingAskUser) {
    return 'passthrough';
  }

  // Des fichiers ne peuvent pas répondre à un widget : laisser passer le
  // message créerait un nouveau tour utilisateur par-dessus une question en
  // attente (historique modèle incohérent, widget suspendu en base).
  if (params.hasReadyAttachments) {
    return 'reject-attachments-only';
  }

  if (params.trimmedInput) {
    return 'intercept-text';
  }

  return 'passthrough';
}

export function buildWidgetAskUserOutput(
  values: string[],
  options: AskUserToolOptionDTO[],
): AskUserToolOutputDTO {
  const labels = values
    .map((value) => options.find((option) => option.value === value)?.label)
    .filter((label): label is string => Boolean(label));

  return {
    labels: labels.length > 0 ? labels : undefined,
    source: 'widget',
    values,
  };
}

export function buildAnsweredInTextOutput(text: string): AskUserToolOutputDTO {
  return {
    source: 'answered_in_text',
    text: text.trim(),
    values: [],
  };
}

export function buildFreeTextWidgetOutput(text: string): AskUserToolOutputDTO {
  return {
    labels: [text],
    source: 'widget',
    text,
    values: [],
  };
}

export function isToolContinuationAlreadyAppliedError(code: string | null) {
  return code === CHAT_ERROR_CODES.TOOL_CONTINUATION_ALREADY_APPLIED;
}

export function parseAskUserToolInput(input: unknown): AskUserToolInputDTO | null {
  if (!input || typeof input !== 'object') {
    return null;
  }

  const candidate = input as Partial<AskUserToolInputDTO>;

  if (
    typeof candidate.question !== 'string'
    || !Array.isArray(candidate.options)
    || candidate.options.length === 0
  ) {
    return null;
  }

  return {
    allowFreeText: candidate.allowFreeText,
    allowMultiple: candidate.allowMultiple,
    options: candidate.options,
    question: candidate.question,
  };
}

export function parseAskUserToolOutput(output: unknown): AskUserToolOutputDTO | null {
  if (!output || typeof output !== 'object') {
    return null;
  }

  const candidate = output as Partial<AskUserToolOutputDTO>;

  if (
    (candidate.source !== 'widget' && candidate.source !== 'answered_in_text')
    || !Array.isArray(candidate.values)
  ) {
    return null;
  }

  return {
    labels: candidate.labels,
    source: candidate.source,
    text: candidate.text,
    values: candidate.values,
  };
}
